import { Bell, UserPlus } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { useAdminNotifications } from "@/hooks/useAdminNotifications";

export const AdminNotificationsDropdown = () => {
  const { data, isLoading } = useAdminNotifications();
  
  if (isLoading || !data) {
    return null;
  }

  const { totalCount, unclaimedCount, matchingCount, embedCount, pendingRegistrationsCount = 0 } = data;
  const hasNotifications = totalCount > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="relative p-2 hover:bg-gray-100 rounded-full transition-colors">
        <Bell className="w-5 h-5 text-gray-700" />
        {totalCount > 0 && (
          <Badge 
            variant="destructive" 
            className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-1 text-xs"
          >
            {totalCount > 99 ? '99+' : totalCount}
          </Badge>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="w-80 bg-white border border-gray-100/50 shadow-lg rounded-lg z-50 p-2 mt-2"
        align="end"
      >
        <div className="px-3 py-2 font-semibold text-sm border-b">
          Admin Notiser
        </div>
        
        {!hasNotifications ? (
          <div className="px-3 py-4 text-sm text-gray-500 text-center">
            Inga väntande uppgifter
          </div>
        ) : (
          <div className="py-1">
            {unclaimedCount > 0 && (
              <DropdownMenuItem asChild>
                <Link 
                  to="/admin/dashboard#unclaimed-messages"
                  className="flex items-center gap-3 px-3 py-3 cursor-pointer"
                >
                  <span className="text-xl">📨</span>
                  <span className="text-sm text-dark-blue">
                    {unclaimedCount} väntande {unclaimedCount === 1 ? 'meddelande' : 'meddelanden'}
                  </span>
                </Link>
              </DropdownMenuItem>
            )}
            {matchingCount > 0 && (
              <DropdownMenuItem asChild>
                <Link 
                  to="/admin/dashboard#matching"
                  className="flex items-center gap-3 px-3 py-3 cursor-pointer"
                >
                  <span className="text-xl">🎯</span>
                  <span className="text-sm text-dark-blue">
                    {matchingCount} {matchingCount === 1 ? 'förfrågan' : 'förfrågningar'} att matcha
                  </span>
                </Link>
              </DropdownMenuItem>
            )}
            {embedCount > 0 && (
              <DropdownMenuItem asChild>
                <Link 
                  to="/admin/dashboard#embeds"
                  className="flex items-center gap-3 px-3 py-3 cursor-pointer"
                >
                  <span className="text-xl">🔗</span>
                  <span className="text-sm text-dark-blue">
                    {embedCount} {embedCount === 1 ? 'ny embed-förfrågan' : 'nya embed-förfrågningar'}
                  </span>
                </Link>
              </DropdownMenuItem>
            )}
            {pendingRegistrationsCount > 0 && (
              <DropdownMenuItem asChild>
                <Link 
                  to="/admin/dashboard#pending"
                  className="flex items-center gap-3 px-3 py-3 cursor-pointer"
                >
                  <UserPlus className="w-5 h-5 text-dark-blue" />
                  <span className="text-sm text-dark-blue">
                    {pendingRegistrationsCount} {pendingRegistrationsCount === 1 ? 'väntande registrering' : 'väntande registreringar'}
                  </span>
                </Link>
              </DropdownMenuItem>
            )}
          </div>
        )}
        
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link 
            to="/admin/dashboard"
            className="w-full text-center px-3 py-2 text-sm text-blue-600 hover:bg-gray-50 rounded cursor-pointer"
          >
            Öppna Admin Dashboard
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
